// @mui
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAuthContext } from 'src/auth/hooks';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function Layout({ children, image, title }) {
  const { method } = useAuthContext();

  const theme = useTheme();

  const upMd = useResponsive('up', 'md');

  const renderLogo = (
    <Logo
      sx={{
        zIndex: 9,
        position: 'absolute',
        m: { xs: 2, md: 5 },
      }}
    />
  );

  const renderContent = (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        flex: { xs: '1 1 100%', md: '0 0 42%' },
        px: { xs: 3, md: 8 },
        py: { xs: 4, md: 6 },
        overflowY: 'auto',
      }}
    >
      <Box sx={{ width: 1, maxWidth: 440 }}>
        {children}
      </Box>
    </Stack>
  );

  const features = [
    { icon: 'solar:users-group-rounded-bold-duotone', label: 'Talent Management', desc: 'Manage actors, models & crew in one place' },
    { icon: 'solar:map-point-bold-duotone', label: 'Location Scouting', desc: 'Organise and share locations with clients' },
    { icon: 'solar:document-text-bold-duotone', label: 'Smart Proposals', desc: 'Build & send branded proposals instantly' },
    { icon: 'solar:chart-2-bold-duotone', label: 'Project Tracking', desc: 'Track budgets, tasks and timelines' },
  ];

  const renderSection = (
    <Stack
      justifyContent="center"
      sx={{
        flex: '0 0 58%',
        px: 8,
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(145deg, ${theme.palette.primary.darker} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.dark} 100%)`,
      }}
    >
      {/* Decorative blobs */}
      <Box sx={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', bgcolor: alpha(theme.palette.common.white, 0.05) }} />
      <Box sx={{ position: 'absolute', bottom: -100, left: -60, width: 400, height: 400, borderRadius: '50%', bgcolor: alpha(theme.palette.common.white, 0.04) }} />
      <Box sx={{ position: 'absolute', top: '40%', right: -40, width: 180, height: 180, borderRadius: '50%', bgcolor: alpha(theme.palette.secondary.light, 0.08) }} />

      {/* Headline */}
      <Typography variant="h3" sx={{ color: 'common.white', fontWeight: 800, lineHeight: 1.2, mb: 1.5 }}>
        Your complete<br />Media CRM
      </Typography>
      <Typography variant="body1" sx={{ color: alpha(theme.palette.common.white, 0.65), mb: 6, maxWidth: 340 }}>
        Streamline productions — from talent and locations to proposals and billing.
      </Typography>

      {/* Feature list */}
      <Stack spacing={3}>
        {features.map((f) => (
          <Stack key={f.label} direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: alpha(theme.palette.common.white, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Iconify icon={f.icon} width={24} sx={{ color: 'common.white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 700 }}>{f.label}</Typography>
              <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.55) }}>{f.desc}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Stack
      component="main"
      direction="row"
      sx={{
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {renderLogo}

      {upMd && renderSection}

      {renderContent}
    </Stack>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  title: PropTypes.string,
};
